import { HiUsers, HiHeart } from "react-icons/hi";
import {
  HiChartBar,
  HiArrowTrendingDown,
  HiArrowTrendingUp,
} from "react-icons/hi2";
import { HiBolt } from "react-icons/hi2";

const HEALTH_COLORS = {
  "At Risk": "bg-red-50 text-red-700",
  Healthy: "bg-emerald-50 text-emerald-700",
  "Needs Attention": "bg-amber-50 text-amber-700",
};

const CONFIDENCE_COLORS = {
  High: "bg-emerald-50 text-emerald-700",
  Med: "bg-amber-50 text-amber-700",
  Low: "bg-red-50 text-red-700",
};

const MetricCard = ({ icon: Icon, iconColor, label, value, change }) => {
  const hasChange = change != null;
  const isPositive = hasChange && change > 0;
  const isNeutral = hasChange && change === 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className={`size-4 ${iconColor}`} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tracking-tight text-gray-900">
          {value.toLocaleString()}
        </span>
        {hasChange && !isNeutral && (
          <span
            className={`flex items-center gap-0.5 text-xs font-semibold ${
              isPositive ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {isPositive ? (
              <HiArrowTrendingUp className="size-3" />
            ) : (
              <HiArrowTrendingDown className="size-3" />
            )}
            {isPositive ? "+" : ""}
            {change}%
          </span>
        )}
        {isNeutral && (
          <span className="text-xs font-semibold text-gray-400">0%</span>
        )}
      </div>
    </div>
  );
};

const HealthCard = ({ health }) => {
  const healthClass =
    HEALTH_COLORS[health.status] ?? "bg-gray-100 text-gray-600";
  const confidenceClass =
    CONFIDENCE_COLORS[health.confidence] ?? "bg-gray-100 text-gray-600";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <HiHeart className="size-4 text-red-500" />
        <span className="text-xs text-gray-500">Health Score</span>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${healthClass}`}
          >
            {health.status}
          </span>
          {health.confidence && (
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${confidenceClass}`}
              title="Confidence in health score calculation"
            >
              <span className="mr-1 size-1.5 rounded-full bg-current opacity-70" />
              {health.confidence} confidence
            </span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span
            className="text-[11px] text-gray-400"
            title="Most recent product usage event recorded from your usage integration"
          >
            Last Activity
          </span>
          <span className="text-xs font-medium text-gray-700">
            {health.lastActivity}
          </span>
        </div>
      </div>
    </div>
  );
};

export const ProductUsageSection = ({ usage }) => (
  <div>
    <h2 className="mb-4 text-base font-semibold text-gray-900">
      Product Usage
    </h2>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        icon={HiUsers}
        iconColor="text-blue-500"
        label="Daily Active Users"
        value={usage.dau.value}
        change={usage.dau.change}
      />
      <MetricCard
        icon={HiBolt}
        iconColor="text-violet-500"
        label="Weekly Active Users"
        value={usage.wau.value}
        change={usage.wau.change}
      />
      <MetricCard
        icon={HiChartBar}
        iconColor="text-emerald-500"
        label="Monthly Active Users"
        value={usage.mau.value}
        change={usage.mau.change}
      />
      <HealthCard health={usage.health} />
    </div>
  </div>
);
